import { DataQuality } from './monitoringTypes';

export enum DateRangeZoomLevel {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export type SiteTimezoneResponse = {
  timezone: string;
};

export enum BatteryStatusValue {
  'Savings Mode',
  'Full Backup',
  'Self-consumption',
}

export enum GridStatusValue {
  'On Grid',
  'Off Grid',
  'Unknown',
}

export enum SolarEdgeStatusValue {
  'Active',
  'Idle',
  'Disabled',
}

export enum PowerFlowStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  OFFLINE = 'Offline',
}

export type PowerFlowDatum = {
  status?: PowerFlowStatus | null;
  power_kw: number;
  state_of_charge_pct?: number | null;
};

export type PowerFlowMonitoringDatum = PowerFlowDatum & Partial<MonitoringDatum<'kw'>>;

export type PowerFlowDataResponse = {
  battery?: PowerFlowMonitoringDatum & { minutes_remaining: number | null };
  grid?: PowerFlowMonitoringDatum;
  home?: PowerFlowMonitoringDatum & { consumption_breakdown: ConsumptionBreakdown | null };
  system?: PowerFlowMonitoringDatum;
  data_quality?: DataQuality;
  power_allocation?: PowerAllocation;
};

export enum BucketSize {
  ALL = 'all',
  MINUTE = 'minute',
  FIFTEEN_MIN = '15min',
  HOUR = 'hour',
  DAY = 'day',
  MONTH = 'month',
  YEAR = 'year',
}

export type MonitoringDatum<T> = {
  value?: number;
  time: Date;
  export_kw?: number;
  import_kw?: number;
  charge_kw?: number;
  discharge_kw?: number;
  charge_kwh?: number;
  discharge_kwh?: number;
  export_kwh?: number;
  import_kwh?: number;
  unit: T;
};

export type ConsumptionBreakdown = {
  pct_from_solar: number;
  pct_from_battery: number;
  pct_from_grid: number;
  is_non_zero_sum: boolean;
  surplus: number;
  deficit: number;
};

export type PowerAllocation = {
  battery_charge_total_kw: number;
  battery_discharge_total_kw: number;
  consumption_from_battery_kw: number;
  consumption_from_grid_kw: number;
  consumption_total_kw: number;
  grid_export_total_kw: number;
  grid_import_total_kw: number;
  production_to_battery_kw: number;
  production_to_grid_kw: number;
  production_to_home_kw: number;
  production_total_kw: number;
};

export enum ChartQueryType {
  POWER,
  ENERGY,
}

export type EnergyUnit = 'Wh' | 'MWh' | 'kWh';
export type PowerUnit = 'W' | 'kW' | 'MW';

export type MonitoringDataParams = {
  aggregation_level?: string;
  start_time: string;
  end_time: string;
  include_energy_allocation?: string;
};

export type AggregatedData<T> = {
  unit: T;
  y: number;
  x?: number;
  y0?: number;
  y0_raw?: number;
  y_raw?: number;
  time: Date;
  labels?: {
    y: string;
    y0: string;
  }[];
};

export interface HomeConsumptionBreakdownDatum {
  timestamp: string;
  from_system_kwh: number;
  from_grid_kwh: number;
  home_consumption_kwh: number;
}

interface HomeConsumptionSummary {
  home_consumption_total_kwh: number;
  from_grid_total_kwh: number;
  from_system_total_kwh: number;
  pct_from_grid: number; // number 0-100
  pct_from_system: number; // number 0-100
}

export type HomeConsumptionBreakdownResponse = {
  monitoring_data: Array<HomeConsumptionBreakdownDatum> | null;
  summary: HomeConsumptionSummary | null;
};
